import React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import { RouteComponentProps, withRouter } from "react-router";
import { ToxnotComponent } from "../core/BaseApplicationComponents/ToxnotComponent";
import { ToxnotComponentProperties } from "../core/BaseApplicationComponents/ToxnotComponent";
import { ToxnotServiceClient } from "../../store/ToxnotServiceClient";
import { ClientTeamRequestGetResult } from "../../store/Models/Api/Consultants/ClientTeamRequestGetResult";

import "./styles.css"
import { LoadingSpinner } from "../core/LoadingSpinner";
import { LoadingSpinnerSize } from "../core/LoadingSpinner";
import { ResultType } from "../../store/Models/Api/ApiResponse";
import NonSignedInContentLayout from "../Layouts/NonSignedInContentLayout";

type Props =
    ToxnotComponentProperties &
    RouteComponentProps<{ code: string }>;

enum UiState {
    Loading,
    Failed,
    Ready
}

interface LocalState {
    uiState: UiState;
    request: ClientTeamRequestGetResult | null;
    errorMessage: string | null
}

// ===============================================================================

/**
 * Main view component for the "view a consultant's request" workflow. Consultants
 * can send a request link to somone, requesting that they allow the consultant
 * to join their team.
 *
 * This shows a simple landing view for a user who follows the link. If the user is already
 * signed in to their Toxnot account, they are simply redirected into the app on the Team page,
 * to review & accept the request.
 *
 * Otherwise this view shows them the request & provides sign in & sign up buttons that will
 * take the user through a workflow to land on the Team page to review & accept the request.
 *
 * The activation code for looking up & validating the request is supplied in the URL - /consult/:code
 */
class Consult extends ToxnotComponent<Props, LocalState> {
    private readonly _serviceClient: ToxnotServiceClient;

    // ===============================================================================

    public constructor(props: Props) {
        super(props);

        this._serviceClient = new ToxnotServiceClient();

        this.state = {
            uiState: UiState.Loading,
            request: null,
            errorMessage: null
        };
    }

    // ===============================================================================

    /**
     * @returns the activation code that was supplied via the URL
     */
    private getCode() {
        return decodeURIComponent(this.props.match.params.code.trim());
    }

    // ===============================================================================

    /**
     * Take the provided code in the URI & hit the API to look up the request
     * from the code. Then set the state up w/ the request data.
     *
     * if the user is already logged in, just redirect them straight to the Team
     * page within the app to view & accept the request.
     */
    public async componentDidMount() {
        const code = this.getCode();

        if (code.length === 0) {
            // invalid url, just send the user back to the homepage
            window.location.href = "/";
            return;
        }

        if (this.props.appServices.userContext !== null) {
            // the user is logged in already, take them to the Team view where they can
            // view & accept the request.
            window.location.href = `/Account/Team?consultantCode=${encodeURIComponent(code)}`;
            return;
        }

        // fetch the request

        const result = await this._serviceClient.clientTeamRequestGet(code);

        if (!result.success || (result.result !== ResultType.Ok)) {
            this.setState({
                uiState: UiState.Failed,
                errorMessage: this.localizeString("Sorry, this invitation is no longer valid.")
            });
            return;
        }

        this.setState({
            uiState: UiState.Ready,
            request: result.data!
        });
    }

    // ===============================================================================

    public render() {
        let content: JSX.Element;

        if (this.state.uiState === UiState.Loading) {
            content = <LoadingSpinner size={LoadingSpinnerSize.Medium} />
        }
        else if (this.state.uiState === UiState.Failed) {
            content = <div className="alert alert-warning">{this.state.errorMessage!}</div>;
        }
        else {
            content = (
                <div>
                    <p>
                        {this.localizeString("A consultant has asked to work with you on Toxnot:")}
                    </p>
                    <p className="team-name">
                        {this.state.request!.teamName}
                    </p>
                    <p className="cta-text">
                        {this.localizeString("Sign In or Sign Up for Free to accept this request.")} <br />
                    </p>
                    <p className="actions">
                        <button type="button" className="btn button-white" onClick={e => this.signInButton_click()}>{this.localizeString("Sign In")}</button>
                        {" "}
                        <button type="button" className="btn button-complement" onClick={e => this.signUpButton_click()}>{this.localizeString("Sign Up")}</button>
                    </p>
                </div>
            );
        }

        return (
            <NonSignedInContentLayout>
                {content}
            </NonSignedInContentLayout>
        );
    }

    // ===============================================================================

    /**
     * returns the URL that puts the user on the Team page to review & accept the request.
     */
    private getAcceptUrl = () => `${this.props.appServices.config.toxnotWebAppUrlEndpoint}/Account/Team?consultantCode=${encodeURIComponent(this.getCode())}`;

    // ===============================================================================

    //
    // UI Events
    //

    private signInButton_click = () => {
        window.location.href = `/Account/SignIn?returnUrl=${encodeURIComponent(this.getAcceptUrl())}`;
    }

    // ===============================================================================

    private signUpButton_click = () => {
        window.location.href = `/Account/SignUp?urlReferrer=${encodeURIComponent(this.getAcceptUrl())}`;
    }
}

// ===============================================================================

export default connect(
    // Selects which state properties are merged into the component's props
    (state: ApplicationState) => ({
        appServices: state.appServices
    })
)(withRouter(Consult) as any);
